import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { formatDrinkDisplayName } from '@/lib/displayName';
import type { CatalogRow } from '@/lib/catalog';
import {
  matchesSearchBlob,
  searchBlob,
  searchRank,
} from '@/lib/searchText';

const MAX_RESULTS = 60;

export function DrinkCombobox({
  drinks,
  selectedIds,
  disabled,
  onPick,
}: {
  drinks: CatalogRow[];
  selectedIds: string[];
  disabled?: boolean;
  onPick: (id: string) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const results = useMemo(() => {
    const q = query.trim();
    type Hit = { row: CatalogRow; alreadySelected: boolean };
    const matched: Hit[] = [];
    for (const row of drinks) {
      const name = formatDrinkDisplayName(row.product.name);
      const brand = formatDrinkDisplayName(row.product.brand ?? '');
      const blob = searchBlob(
        name,
        brand,
        row.product.subtype,
        row.product.category,
        row.product.slug,
        ...row.product.aliases,
      );
      if (!matchesSearchBlob(blob, q)) continue;
      matched.push({ row, alreadySelected: selected.has(row.product.id) });
    }
    if (q) {
      matched.sort((a, b) => {
        // Prefer drinks not already selected, then relevance.
        if (a.alreadySelected !== b.alreadySelected) {
          return a.alreadySelected ? 1 : -1;
        }
        const ra = searchRank(
          formatDrinkDisplayName(a.row.product.name),
          formatDrinkDisplayName(a.row.product.brand ?? ''),
          q,
        );
        const rb = searchRank(
          formatDrinkDisplayName(b.row.product.name),
          formatDrinkDisplayName(b.row.product.brand ?? ''),
          q,
        );
        if (ra !== rb) return ra - rb;
        return formatDrinkDisplayName(a.row.product.name).localeCompare(
          formatDrinkDisplayName(b.row.product.name),
          undefined,
          { sensitivity: 'base' },
        );
      });
    }
    return matched.slice(0, MAX_RESULTS);
  }, [drinks, query, selected]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function pick(id: string) {
    onPick(id);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const hit = results[activeIndex];
      if (hit && !hit.alreadySelected) pick(hit.row.product.id);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const showList = open && !disabled;
  const emptyHint = query.trim()
    ? 'No matching drinks'
    : 'Type a name to search…';

  return (
    <div className="drink-combobox" ref={rootRef}>
      <label htmlFor={`${listId}-input`}>Add drink</label>
      <input
        ref={inputRef}
        id={`${listId}-input`}
        className="search-input"
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls={`${listId}-list`}
        aria-autocomplete="list"
        aria-activedescendant={
          showList && results[activeIndex]
            ? `${listId}-opt-${results[activeIndex].row.product.id}`
            : undefined
        }
        placeholder="Search drinks (e.g. dogfish, 60min)…"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {showList && (
        <ul
          id={`${listId}-list`}
          className="drink-combobox__list"
          role="listbox"
          aria-label="Matching drinks"
        >
          {results.length === 0 ? (
            <li className="drink-combobox__empty" role="presentation">
              {emptyHint}
            </li>
          ) : (
            results.map((hit, index) => {
              const { row, alreadySelected } = hit;
              const name = formatDrinkDisplayName(row.product.name);
              const active = index === activeIndex;
              return (
                <li key={row.product.id} role="presentation">
                  <button
                    type="button"
                    id={`${listId}-opt-${row.product.id}`}
                    role="option"
                    aria-selected={active}
                    aria-disabled={alreadySelected || undefined}
                    disabled={alreadySelected}
                    className={
                      active
                        ? 'drink-combobox__option drink-combobox__option--active'
                        : 'drink-combobox__option'
                    }
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      if (!alreadySelected) pick(row.product.id);
                    }}
                  >
                    <span>{name}</span>
                    <span className="muted drink-combobox__meta">
                      {alreadySelected
                        ? 'Already in compare'
                        : `${row.product.subtype}${
                            row.product.abvPercent != null
                              ? ` · ${row.product.abvPercent}%`
                              : ''
                          }`}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
